import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'vawena-exit-popup-dismissed';
const DISMISS_DAYS = 7;

function shouldShow(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const ts = parseInt(raw, 10);
    return Date.now() - ts > DISMISS_DAYS * 86400 * 1000;
  } catch {
    return true;
  }
}

function dismiss() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {}
}

// Pages where we don't show the popup (quiz itself, legal, auth)
const EXCLUDED_PATHS = ['/quiz', '/login', '/signup', '/privacy-policy', '/terms', '/get-started'];

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const triggered = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const excluded = EXCLUDED_PATHS.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (excluded) return;
    if (!shouldShow()) return;

    // Reset trigger on route change so it can fire once per page
    triggered.current = false;

    const handleMouseLeave = (e: MouseEvent) => {
      if (triggered.current) return;
      if (e.clientY <= 20) {
        triggered.current = true;
        setVisible(true);
      }
    };

    // Small delay so it doesn't fire on initial load
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [location.pathname, excluded]);

  const handleClose = () => {
    dismiss();
    setVisible(false);
  };

  const handleCTA = () => {
    dismiss();
    setVisible(false);
    navigate('/quiz');
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(2,13,48,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#020D30] px-8 pt-8 pb-6 text-center">
          <img
            src="/assets/logo/vawena-logo.png"
            alt="VaWeNa"
            className="h-8 w-auto mx-auto mb-5"
          />
          <h2 className="text-2xl font-extrabold text-white leading-tight">
            Before you go — do you know your investment readiness score?
          </h2>
        </div>

        {/* Blue bar */}
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg,#1d4ed8,#2563eb,#3b82f6)' }} />

        {/* Body */}
        <div className="bg-white px-8 py-6 text-center">
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            Most real estate investors overestimate their buying power by 20–30%.
            Our free 2-minute quiz tells you exactly where you stand — no signup needed.
          </p>

          <button
            onClick={handleCTA}
            className="w-full py-3.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-base transition-colors"
          >
            Take the Free Quiz →
          </button>

          <button
            onClick={handleClose}
            className="mt-3 w-full py-2.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            No thanks, I'll figure it out myself
          </button>
        </div>

        {/* Close X */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
